import { useStacklineTheme } from '@stackline/ui';
import React from 'react';
import {
  ICON_STYLES,
  TopicFilter
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import {
  useTopicBuilderActions,
  useTopicBuilderFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import GenericSidebarFilter, {
  GenericSidebarFilterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';
import { SlSidebarScrollableContainer } from 'src/components/BeaconV2Components/SlSidebar';
import {
  DisabledCheckbox,
  SlDisabledExcludeSelectedCheckbox,
  SlExcludeSelectedCheckbox,
  SlSelectedCheckbox
} from 'src/components/SvgIcons';
import { useAppSelector } from 'src/utils/Hooks';

interface BrandSidebarFilterProps extends GenericSidebarFilterProps {
  readOnly?: boolean;
}

const BrandSidebarFilter = (props: BrandSidebarFilterProps) => {
  const theme = useStacklineTheme();

  const { filters } = useTopicBuilderFilters();
  const { setFilters } = useTopicBuilderActions();

  const includedBrands = filters[TopicFilter.IncludedBrandIds];
  const excludedBrands = filters[TopicFilter.ExcludedBrandIds];

  const isIncluding = includedBrands.length > 0 || (includedBrands.length === 0 && excludedBrands.length === 0);
  const selectedBrands = isIncluding ? includedBrands : excludedBrands;

  const shouldRender = selectedBrands?.length > 0 || !props.readOnly;

  const brandOptions = useAppSelector((state) =>
    state.brandsFollowing
      .filter((brand) => brand?.id !== 0)
      .map((brand) => ({
        ...brand,
        selected: selectedBrands.some((selectedBrand) => Number(selectedBrand) === brand.id)
      }))
  );

  /**
   * Search input for filtering down categories
   */
  const [currentInput, setCurrentInput] = React.useState<string>('');

  /**
   * Toggle mode for include/exclude; determines which icon to render in the renderItems function.
   */
  const [toggleMode, setToggleMode] = React.useState<'include' | 'exclude'>(isIncluding ? 'include' : 'exclude');

  React.useEffect(() => {
    setToggleMode(isIncluding ? 'include' : 'exclude');
  }, [isIncluding]);

  /**
   * Handles the selection of a category.
   * Updates the selected state of the category and filters down the available subcategories based on the selected categories.
   */
  const handleItemSelect = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const id = Number(value);

      switch (toggleMode) {
        case 'include':
          setFilters(({ toggle }) => toggle({ key: TopicFilter.IncludedBrandIds, value: id }));
          break;
        case 'exclude':
          setFilters(({ toggle }) => toggle({ key: TopicFilter.ExcludedBrandIds, value: id }));
          break;
        default:
      }
    },
    [setFilters, toggleMode]
  );

  const getIcon = React.useCallback(() => {
    switch (toggleMode) {
      case 'include': {
        if (props.readOnly) {
          return <DisabledCheckbox style={ICON_STYLES} />;
        } else {
          return <SlSelectedCheckbox style={ICON_STYLES} />;
        }
      }

      case 'exclude': {
        if (props.readOnly) {
          return <SlDisabledExcludeSelectedCheckbox style={ICON_STYLES} />;
        } else {
          return <SlExcludeSelectedCheckbox style={ICON_STYLES} />;
        }
      }
      default:
        return null;
    }
  }, [toggleMode, props.readOnly]);

  /**
   * Renders the list of items as checkboxes.
   */
  const renderItems = React.useCallback(() => {
    return (
      <SlSidebarScrollableContainer>
        {brandOptions
          ?.filter((brand) => brand.name.toLowerCase().includes(currentInput.toLowerCase()))
          .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1)) // sort by checked items
          .map((brand, index) => (
            <CheckboxItem
              onClick={handleItemSelect}
              disabled={props.readOnly}
              checkedIcon={getIcon()}
              value={brand.id}
              key={index}
              label={brand.name}
              theme={theme}
              checked={brand.selected}
            />
          ))}
      </SlSidebarScrollableContainer>
    );
  }, [getIcon, brandOptions, currentInput, theme, handleItemSelect, props.readOnly]);

  /**
   * Handles the change of the input to filter down the categories.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  /**
   * Clears the selected brands
   */
  const handleClear = () => {
    switch (toggleMode) {
      case 'include':
        setFilters(({ clear }) => clear({ key: TopicFilter.IncludedBrandIds }));
        break;
      case 'exclude':
        setFilters(({ clear }) => clear({ key: TopicFilter.ExcludedBrandIds }));
        break;
      default:
    }

    setCurrentInput('');
  };
  const handleToggle = (toggle: 'include' | 'exclude') => {
    setToggleMode(toggle);

    switch (toggle) {
      case 'include':
        setFilters(({ replace }) =>
          replace({ target: TopicFilter.IncludedBrandIds, source: TopicFilter.ExcludedBrandIds })
        );
        break;
      case 'exclude':
        setFilters(({ replace }) =>
          replace({ target: TopicFilter.ExcludedBrandIds, source: TopicFilter.IncludedBrandIds })
        );
        break;
      default:
    }
  };

  return (
    <>
      {shouldRender && (
        <GenericSidebarFilter
          {...props}
          includeInput={!props.readOnly}
          includeToggles={!props.readOnly}
          handleToggle={handleToggle}
          toggleMode={toggleMode}
          includeClear={selectedBrands.length > 0 && !props.readOnly}
          renderItems={renderItems}
          handleClear={handleClear}
          sidebarInputProps={{
            ...props.sidebarInputProps,
            value: currentInput,
            onChange: handleChange
          }}
        />
      )}
    </>
  );
};

export default BrandSidebarFilter;
